import React, {
  FormEvent,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import IconFilter from '@assets/images/icon_filter.svg';
import {
  ApplyButton,
  FilterActions,
  FilterBar,
  FilterFields,
  FilterItemContainer,
  ResetButton,
} from '@components/filter/FilterStyle';
import useWindow from '@lib/hooks/useWindow';
import { breakpoints, media } from '@styles/mediaQuery';
import theme from '@theme/index';
import { normalizeRenderableItems } from '@util/array';
import throttle from 'lodash/throttle';
import styled from 'styled-components';

/**
 * PageWithFilter - 통합 필터 시스템을 사용하는 페이지 레이아웃
 *
 * 페이지 타이틀과 필터 시스템을 통합 제공한다.
 * 필터는 페이지 상단에 접이식 가로 바로 배치되며, 본문(테이블)은 항상 전체 너비를 사용한다.
 *
 * @param title - 페이지 타이틀
 * @param itemsRightSideTitle - 타이틀 우측 요소들
 * @param children - 메인 컨텐츠 영역
 * @param filters - 필터 아이템 배열 (FilterItem 컴포넌트들)
 * @param onApplyFilter - 필터 적용 시 실행할 함수
 * @param onResetFilter - 필터 초기화 시 실행할 함수
 * @param filterWidth - (deprecated) 가로 바 레이아웃에서는 사용되지 않음
 * @param refetchOnFilterChange - 필터 값 변경 시 자동으로 refetch 실행 여부 (기본: false)
 * @param filterDependencies - 필터 변경을 감지하기 위한 의존성 배열 (refetchOnFilterChange가 true일 때만 사용)
 * @param throttleMs - throttle 주기 (밀리초, 기본: 1000ms)
 */

interface Props {
  title?: string;
  itemsRightSideTitle?: React.ReactElement | React.ReactElement[];
  children: ReactNode;
  filters?: ReactNode | ReactNode[];
  mobileQuickFilters?: ReactNode | ReactNode[];
  onApplyFilter?: () => void;
  onResetFilter?: () => void;
  filterWidth?: string;
  refetchOnFilterChange?: boolean;
  filterDependencies?: any[];
  throttleMs?: number;
}

const PageWithFilter = ({
  title,
  itemsRightSideTitle,
  children,
  filters,
  mobileQuickFilters,
  onApplyFilter,
  onResetFilter,
  refetchOnFilterChange = false,
  filterDependencies,
  throttleMs = 1000,
}: Props) => {
  const windowSize = useWindow();
  const rightTitleItems = normalizeRenderableItems(itemsRightSideTitle);
  const filterItems = normalizeRenderableItems(filters);
  const mobileQuickFilterItems = normalizeRenderableItems(mobileQuickFilters);
  const filterDependencyList = Array.isArray(filterDependencies) ? filterDependencies : [];

  // 최신 onApplyFilter를 참조하기 위한 ref
  const onApplyFilterRef = useRef(onApplyFilter);
  useEffect(() => {
    onApplyFilterRef.current = onApplyFilter;
  }, [onApplyFilter]);

  // Throttle된 필터 적용 함수
  const throttledApplyRef = useRef(
    throttle(() => {
      onApplyFilterRef.current?.();
    }, throttleMs),
  );

  const isMobile = useMemo(() => {
    const viewportWidth = windowSize?.actualWidth || windowSize?.width;
    if (!windowSize || !viewportWidth || viewportWidth <= breakpoints.mobile) return true;
    return false;
  }, [windowSize]);

  // 필터 펼침 상태 (기본 펼침, 좁은 화면에서는 최초 1회 접힘)
  const [isFilterOpen, setIsFilterOpen] = useState(true);
  const didInitOpenState = useRef(false);
  useEffect(() => {
    if (didInitOpenState.current || !windowSize) return;
    didInitOpenState.current = true;
    const viewportWidth = windowSize.actualWidth || windowSize.width || 0;
    if (viewportWidth <= breakpoints.tablet) setIsFilterOpen(false);
  }, [windowSize]);

  const toggleFilter = useCallback(() => setIsFilterOpen((prev) => !prev), []);

  const handleApplyFilter = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onApplyFilter?.();
    // 좁은 화면에서는 적용 후 바를 접어 테이블을 넓게 본다
    if (isMobile) setIsFilterOpen(false);
  };

  const handleMobileQuickFilter = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onApplyFilter?.();
  };

  const handleResetFilter = () => {
    const filterResetEvent = new CustomEvent('reset-filter');
    window.dispatchEvent(filterResetEvent);
    onResetFilter?.();
  };

  // 필터 값 변경 시 throttle 적용하여 자동 refetch
  useEffect(() => {
    if (!refetchOnFilterChange) return;
    if (filterDependencyList.length === 0) return;
    throttledApplyRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refetchOnFilterChange, ...filterDependencyList]);

  const hasFilters = filterItems.length > 0;
  const hasMobileQuickFilters = mobileQuickFilterItems.length > 0;

  return (
    <PageContent>
      {(title || rightTitleItems.length > 0 || hasFilters) && (
        <PageTitleArea>
          {title && <h1>{title}</h1>}
          <RightSideTitleArea>
            {hasFilters && (
              <FilterToggleButton type="button" onClick={toggleFilter} $enabled={isFilterOpen}>
                <img src={IconFilter} alt="filter" />
                <span>필터</span>
              </FilterToggleButton>
            )}
            {rightTitleItems.map((item, index) =>
              React.isValidElement(item) ? (
                React.cloneElement(item as React.ReactElement, { key: `page-right-side-${index}` })
              ) : (
                <React.Fragment key={`page-right-side-${index}`}>{item}</React.Fragment>
              ),
            )}
          </RightSideTitleArea>
        </PageTitleArea>
      )}

      {hasMobileQuickFilters && isMobile && !isFilterOpen && (
        <MobileQuickFilterForm
          data-testid="mobile-quick-filter-form"
          onSubmit={handleMobileQuickFilter}>
          <MobileQuickFilterFields>
            {mobileQuickFilterItems.map((filter, idx) => (
              <div className="quick-filter-field" key={idx}>
                {filter}
              </div>
            ))}
          </MobileQuickFilterFields>
          <MobileQuickSearchButton type="submit">검색</MobileQuickSearchButton>
        </MobileQuickFilterForm>
      )}

      {hasFilters && isFilterOpen && (
        <FilterBar onSubmit={handleApplyFilter}>
          <FilterFields>
            {filterItems.map((filter, idx) => (
              <div className="filter-field" key={idx}>
                {filter}
              </div>
            ))}
          </FilterFields>
          <FilterActions>
            <ResetButton type="button" onClick={handleResetFilter}>
              초기화
            </ResetButton>
            <ApplyButton type="submit">적용</ApplyButton>
          </FilterActions>
        </FilterBar>
      )}

      {children}
    </PageContent>
  );
};

export default PageWithFilter;

// Styled Components

const FilterToggleButton = styled.button<{ $enabled: boolean }>`
  padding: 0.5rem 1rem;
  border-radius: 0.25rem;
  background: ${({ $enabled }) => ($enabled ? theme.primary : theme.white)};
  color: ${({ $enabled }) => ($enabled ? theme.white : theme.neutralGray[700])};
  border: 1px solid ${({ $enabled }) => ($enabled ? theme.primary : theme.neutralGray[300])};
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.375rem;

  img {
    width: 1rem;
    height: 1rem;
    filter: ${({ $enabled }) =>
      $enabled
        ? 'brightness(0) invert(1)'
        : 'brightness(0) saturate(100%) invert(40%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(98%) contrast(90%)'};
  }

  &:hover {
    background: ${({ $enabled }) => ($enabled ? theme.main[500] : theme.neutralGray[100])};
    border-color: ${({ $enabled }) => ($enabled ? theme.main[500] : theme.neutralGray[400])};
  }

  &:active {
    transform: scale(0.98);
  }

  @media (max-width: ${breakpoints.tablet}px) {
    width: 2.5rem;
    height: 2.5rem;
    justify-content: center;
    padding: 0;
    border-radius: 0.625rem;

    span {
      display: none;
    }
  }
`;

const PageContent = styled.div`
  width: 100%;
  padding: 1.5rem;
  background: white;

  box-shadow: 0px 2px 8px rgba(34, 34, 34, 0.15);

  display: flex;
  flex-direction: column;
  gap: 1.5rem;

  h1 {
    font-size: 1.5rem;
    font-weight: bold;
    margin: 0;
  }

  ${media.tablet`
    margin: 0;
    padding: 1rem;
    border-radius: 0;
    gap: 1rem;

    h1 {
      font-size: 1.375rem;
      line-height: 1.3;
    }
  `}
`;

const PageTitleArea = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;

  ${media.tablet`
    min-height: 2.5rem;
    gap: 0.75rem;
  `}
`;

const RightSideTitleArea = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const MobileQuickFilterForm = styled.form`
  display: none;

  ${media.mobile`
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: center;
    gap: 0.5rem;
    padding: 0;

    ${FilterItemContainer} {
      width: 100%;
      min-width: 0;
      padding: 0;
      gap: 0;

      h3 {
        display: none;
      }

      input {
        min-height: 2.75rem;
        padding: 0.625rem 0.875rem;
        font-size: 1rem;
      }
    }

    .quick-filter-field:not(:first-child) {
      grid-column: 1 / -1;

      ${FilterItemContainer} {
        min-height: 2.75rem;
        flex-direction: row;
        justify-content: space-between;
        padding: 0 0.75rem;
        border: 1px solid ${theme.main[200]};
        border-radius: 0.625rem;
        background: ${theme.main[100]};

        h3 {
          display: block;
          color: ${theme.neutralGray[600]};
          font-size: 0.875rem;
          font-weight: 600;
        }
      }
    }
  `}
`;

const MobileQuickFilterFields = styled.div`
  min-width: 0;
  flex: 1;

  .quick-filter-field {
    min-width: 0;
  }

  ${media.mobile`
    display: contents;
  `}
`;

const MobileQuickSearchButton = styled.button`
  flex: 0 0 auto;
  min-width: 4.25rem;
  min-height: 2.75rem;
  padding: 0.625rem 0.875rem;
  border: none;
  border-radius: 0.625rem;
  background: ${theme.primary};
  color: ${theme.white};
  font-size: 0.875rem;
  font-weight: 600;

  ${media.mobile`
    grid-column: 2;
    grid-row: 1;
  `}
`;
